import { useCallback, useRef, useState } from 'react'
import { isErrorReturn } from '@guestgoodie/auth-core'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import Button from '@/components/ui/Button'
import CodeInput from '@/components/ui/CodeInput'
import { useAuth } from '../hooks/useAuth'
import { useErrorToText } from '../hooks/useErrorCode'

type FormData = {
  code: string
}

export default function VerificationCodeForm({
  email,
  password,
  rememberMe
}: {
  email: string
  password: string
  rememberMe: boolean
}) {
  const { t } = useLingui()
  const { client, refresh } = useAuth()
  const getError = useErrorToText()
  const submitRef = useRef<HTMLButtonElement>(null)
  const [serverError, setServerError] = useState<Form.ServerError>()

  // We just send the login again, since this will create a new code when email_verified=false
  const resend = useCallback(async () => {
    return await client.signIn({
      email,
      password,
      remember_me: rememberMe
    })
  }, [client, email, password, rememberMe])

  const submit = useCallback(
    async (data: FormData) => {
      const response = await client.verifyCode(data.code)

      if (isErrorReturn(response)) {
        setServerError({
          type: response.errorCode,
          message: getError(response.errorCode)
        })

        return
      }

      const resendResponse = await resend()

      if (isErrorReturn(resendResponse)) {
        setServerError({
          type: resendResponse.errorCode,
          message: getError(resendResponse.errorCode)
        })

        return
      }

      refresh()
    },
    [client, refresh, getError, resend, setServerError]
  )

  return (
    <Form.Root
      onSubmit={submit}
      serverErrors={
        serverError
          ? {
              root: { serverError }
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <h1 className="text-text text-heading1 text-center">
          <Trans>Verify your email</Trans>
        </h1>
        <Form.Field
          name="code"
          rules={{
            validate: {
              codeLength(code: string) {
                if (code.length !== 6) return t`Code has to have 6 Fields.`
              },
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: props }) => (
            <div className="w-full flex flex-row justify-center">
              <CodeInput
                onComplete={() => submitRef.current?.click()}
                {...props}
              />
            </div>
          )}
        </Form.Field>
        <div className="text-center">
          <Form.Error name="root.serverError" />
        </div>
        <p className="max-w-[600px] text-text text-regular">
          <Trans>
            A verification code was sent to the email {email}, the code is valid
            for 15 minutes. If you didn`t receive a code please check your spam
            folder and if you wrote your email correctly. Else you can try to
            resend the code.
          </Trans>
        </p>
        <div className="flex flex-col justify-evenly gap-4 p-2 md:flex-row">
          <Button
            variant="outline"
            type="button"
            className="flex-1"
            onClick={resend}
          >
            <Trans>Send New Code</Trans>
          </Button>
          <Form.Submit className="flex-1" ref={submitRef}>
            <Trans>Submit</Trans>
          </Form.Submit>
        </div>
      </div>
    </Form.Root>
  )
}
